import { useEffect } from "react";

/** Public production origin used for canonical / og:url (pages are indexable there). */
export const SITE_URL = "https://app.betterdays.fit";

function setTag(selector, create, content) {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) { el = create(); document.head.appendChild(el); created = true; }
  const prev = el.getAttribute(content.attr);
  el.setAttribute(content.attr, content.value);
  return () => { if (created) el.remove(); else if (prev != null) el.setAttribute(content.attr, prev); };
}

/**
 * Manage <title>, meta description, canonical, Open Graph / Twitter tags and
 * JSON-LD structured data for a route. All changes are reverted on unmount.
 * og/twitter images must be absolute https URLs — base64 data URLs and
 * relative paths are useless to scrapers, so they fall back to the default
 * share image until the backend serves hosted product images.
 * @param {{title:string, description:string, path:string, image?:string, jsonLd?:object|object[], type?:string, noindex?:boolean}} opts
 */
export function useSeo({ title, description, path, image, jsonLd, noindex, type }) {
  useEffect(() => {
    const cleanups = [];
    const url = `${SITE_URL}${path || ""}`;
    const img = /^https:\/\//.test(image || "") ? image : `${SITE_URL}/og/landing.jpg`;
    const isProd = typeof window !== "undefined" && window.location.hostname === "app.betterdays.fit";
    const robots = noindex || !isProd ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1";

    const prevTitle = document.title;
    if (title) document.title = title;

    cleanups.push(setTag('meta[name="robots"]', () => { const m = document.createElement("meta"); m.setAttribute("name", "robots"); return m; }, { attr: "content", value: robots }));

    if (description) {
      cleanups.push(setTag('meta[name="description"]', () => { const m = document.createElement("meta"); m.setAttribute("name", "description"); return m; }, { attr: "content", value: description }));
    }
    cleanups.push(setTag('link[rel="canonical"]', () => { const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l; }, { attr: "href", value: url }));

    const og = [
      ["og:title", title], ["og:description", description], ["og:type", type || "website"],
      ["og:url", url], ["og:image", img], ["og:image:alt", title], ["og:site_name", "Better Days"],
      ...(img.includes("/og/") ? [["og:image:width", "1200"], ["og:image:height", "630"]] : []),
    ];
    og.forEach(([prop, value]) => value && cleanups.push(
      setTag(`meta[property="${prop}"]`, () => { const m = document.createElement("meta"); m.setAttribute("property", prop); return m; }, { attr: "content", value })
    ));
    const tw = [["twitter:card", "summary_large_image"], ["twitter:title", title], ["twitter:description", description], ["twitter:image", img]];
    tw.forEach(([name, value]) => value && cleanups.push(
      setTag(`meta[name="${name}"]`, () => { const m = document.createElement("meta"); m.setAttribute("name", name); return m; }, { attr: "content", value })
    ));

    let ld;
    if (jsonLd) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.setAttribute("data-seo-ld", "1");
      ld.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(ld);
    }

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
      if (ld) ld.remove();
    };
  }, [title, description, path, image, jsonLd, noindex, type]);
}
